import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { API_CONFIG } from '@/config/api.config'
import { useUIStore } from '@/stores/ui.store'
import { getCurrentLocale } from '@/i18n'

// Arka plan istekleri (ör. periyodik polling) çubuğu tetiklemesin diye
// istek config'inde { skipLoading: true } gönderilebilir.
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipLoading?: boolean
  }
}

// Consistent storage keys — must match auth.store.ts
const TOKEN_KEY = 'ik_access_token'
const REFRESH_KEY = 'ik_refresh_token'
const USER_KEY = 'ik_user'

function loginPathFromStoredUser(): string {
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (raw) {
      const u = JSON.parse(raw) as { isPortalUser?: boolean }
      if (u?.isPortalUser) return '/login'
    }
  } catch {
    /* ignore */
  }
  return '/login'
}

/** 401 burada genelde “şifre yanlış”dır; refresh denemesi oturumu bozar / yanlış yönlendirir */
function shouldSkipTokenRefreshOn401(config: unknown): boolean {
  const c = config as { url?: string; baseURL?: string } | undefined
  const path = `${c?.baseURL || ''}${c?.url || ''}`.toLowerCase()
  const skip = [
    '/auth/login',
    '/auth/portal-login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password'
  ]
  return skip.some((s) => path.includes(s))
}

function forceSessionEndRedirect() {
  const target = loginPathFromStoredUser()
  clearAuthTokens()
  const onLoginPage = API_CONFIG.loginRoutes.some(
    (route) => window.location.pathname === route
  )
  if (!onLoginPage) {
    window.location.href = target
  }
}

/**
 * Yenileme yalnız sunucu token'ı gerçekten reddettiğinde (401/403) oturumu bitirir.
 * Ağ kopması, zaman aşımı veya 5xx geçicidir — bu durumda oturumu silmek
 * kullanıcıyı sebepsiz yere login'e atar.
 */
function shouldEndSessionAfterFailedRefresh(error: unknown): boolean {
  const status = (error as AxiosError)?.response?.status
  if (status === undefined) return false
  return status === 401 || status === 403
}

let accessToken = localStorage.getItem(TOKEN_KEY) || ''
let refreshTokenValue = localStorage.getItem(REFRESH_KEY) || ''

const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
})

// ─── Global yükleme çubuğu interceptor'ları ─────────────────
// Pinia store, istek anında (uygulama mount edildikten sonra) çözülür.
function trackLoadingStart(config: any) {
  if (config?.skipLoading) return
  config.__loadingTracked = true
  try {
    useUIStore().startLoading()
  } catch {
    /* pinia henüz aktif değilse sessizce geç */
  }
}

function trackLoadingStop(config: any) {
  if (!config?.__loadingTracked) return
  config.__loadingTracked = false
  try {
    useUIStore().stopLoading()
  } catch {
    /* ignore */
  }
}

api.interceptors.request.use(
  (config) => {
    trackLoadingStart(config)
    return config
  },
  (error) => {
    trackLoadingStop(error?.config)
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => {
    trackLoadingStop(response.config)
    return response
  },
  (error: AxiosError) => {
    trackLoadingStop(error?.config)
    return Promise.reject(error)
  }
)

// ─── Token refresh queue (handles concurrent 401s) ──────────
let isRefreshing = false
let failedQueue: Array<{
  onSuccess: (token: string) => void
  onError: (error: any) => void
}> = []

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.onError(error)
    } else {
      promise.onSuccess(token!)
    }
  })
  failedQueue = []
  isRefreshing = false
}

// ─── Request interceptor: attach token ───────────────────────
api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    // Seçili dili backend'e ilet (nestjs-i18n AcceptLanguageResolver)
    config.headers['Accept-Language'] = getCurrentLocale()
    // FormData için boundary ile multipart Content-Type axios'un set etmesine izin ver
    if (config.data instanceof FormData) {
      delete (config.headers as Record<string, unknown>)['Content-Type']
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ─── Response interceptor: handle 401 + token refresh ────────
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    if (shouldSkipTokenRefreshOn401(originalRequest)) {
      return Promise.reject(error)
    }

    // Refresh sonrası tekrar 401 → oturum geçersiz; aksi halde kullanıcı sayfada “takılı” kalıyor
    if (originalRequest._retry) {
      processQueue(error, null)
      forceSessionEndRedirect()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((onSuccess, onError) => {
        failedQueue.push({ onSuccess, onError })
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          originalRequest._retry = true
          return api(originalRequest)
        })
        .catch((err) => Promise.reject(err))
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const response = await axios.post(
        `${API_CONFIG.baseURL}${API_CONFIG.refreshEndpoint}`,
        refreshTokenValue ? { refreshToken: refreshTokenValue } : {},
        { withCredentials: true }
      )

      const newAccessToken = response.data?.data?.accessToken || response.data?.accessToken
      const newRefreshToken = response.data?.data?.refreshToken || response.data?.refreshToken

      if (!newAccessToken) throw new Error('Invalid refresh response')

      setAuthTokens(newAccessToken, newRefreshToken || refreshTokenValue)

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      processQueue(null, newAccessToken)

      return api(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      if (shouldEndSessionAfterFailedRefresh(refreshError)) {
        forceSessionEndRedirect()
      }
      return Promise.reject(refreshError)
    }
  }
)

// ─── Public token helpers (used by auth.store) ───────────────
export function setAuthTokens(access: string, refresh: string) {
  accessToken = access
  refreshTokenValue = refresh
  api.defaults.headers.common.Authorization = `Bearer ${access}`
  localStorage.setItem(TOKEN_KEY, access)
  localStorage.setItem(REFRESH_KEY, refresh)
}

export function clearAuthTokens() {
  accessToken = ''
  refreshTokenValue = ''
  delete api.defaults.headers.common.Authorization
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem('ik_user')
}

export default api
